// src/components/audio/DispositionPill.js
//
// Three-state pill for the reviewer's overall disposition on a submission's
// audio. Mirrors the visual language of CoachingPanel.js severity pills —
// colored backgrounds with translucent fills, animated active state.
//
// Used in AudioAudit's player pane, prominently above the metadata grid.

import React from 'react';
import { Check, AlertCircle, X, Circle } from 'lucide-react';
import { DISPOSITIONS } from './audioNotes';

const ICONS = {
  good:    Check,
  concern: AlertCircle,
  reject:  X,
};

const DispositionPill = ({ value, onChange, isDarkMode, saving }) => {
  const colors = isDarkMode
    ? {
        text:        '#E7EAF0',
        textMuted:   '#6B7180',
        text2:       '#B8BDC9',
        bg:          'rgba(255,255,255,0.02)',
        bgHover:     'rgba(255,255,255,0.05)',
        bdr:         'rgba(255,255,255,0.06)',
      }
    : {
        text:        '#15181D',
        textMuted:   '#6B7180',
        text2:       '#3A3F4A',
        bg:          'rgba(0,0,0,0.02)',
        bgHover:     'rgba(0,0,0,0.04)',
        bdr:         'rgba(0,0,0,0.07)',
      };

  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      gap: '14px',
      padding: '14px 18px',
      background: colors.bg,
      border: `1px solid ${colors.bdr}`,
      borderRadius: '12px',
      fontFamily: "'Outfit', system-ui, sans-serif",
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: '10.5px', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.1em',
          color: colors.textMuted,
        }}>
          Disposition
        </div>
        <div style={{
          fontSize: '12.5px', color: colors.text2, marginTop: '3px',
          lineHeight: 1.4,
        }}>
          {value
            ? `Marked ${value}`
            : 'Listen, then mark this submission.'}
        </div>
      </div>

      <div style={{
        display: 'inline-flex', flexShrink: 0,
        gap: '4px',
        padding: '3px',
        background: colors.bg,
        border: `1px solid ${colors.bdr}`,
        borderRadius: '999px',
      }}>
        {DISPOSITIONS.map((d) => {
          const Icon = ICONS[d.id] || Circle;
          const active = value === d.id;
          return (
            <button
              key={d.id}
              onClick={() => onChange?.(active ? null : d.id)}
              disabled={saving}
              title={`${d.label} — ${d.description}${active ? ' (click again to clear)' : ''}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px',
                borderRadius: '999px',
                border: 'none',
                background: active ? `${d.color}1F` : 'transparent',
                color: active ? d.color : colors.textMuted,
                fontFamily: 'inherit',
                fontSize: '12px',
                fontWeight: active ? 600 : 500,
                cursor: saving ? 'wait' : 'pointer',
                transition: 'background 0.15s ease, color 0.15s ease',
                opacity: saving ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!active && !saving) e.currentTarget.style.background = colors.bgHover;
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = 'transparent';
              }}
            >
              <Icon size={13} />
              {d.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DispositionPill;
